import React, { useEffect, useRef } from "react";
import { useCountUp } from "@/features/investors/hooks/use-count-up";
import { useLoaderProgress } from "@/features/investors/hooks/use-loader-progress";
import { investorsFormatters as fmt } from "@/features/investors/utils/investors-formatters";

const STATUS_MESSAGES = [
    "Securing your connection",
    "Querying the operations index",
    "Aggregating quarterly metrics",
    "Computing retention cohorts",
    "Preparing board insights",
] as const;

const EXIT_TRANSITION_MS = 750;
const COUNT_UP_MS = 900;

export type InvestorsLoaderStats = {
    volumeEur: number;
    operations: number;
    activeAccounts: number;
};

type Props = {
    quarterLabel: string;
    ready: boolean;
    failed: boolean;
    stats: InvestorsLoaderStats | null;
    onExited: () => void;
};

const VbMark: React.FC = () => (
    <svg viewBox="0 0 135 116" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path
            className="vbl-b"
            d="M116.413 59.8825C115.691 62.1539 114.211 64.1157 112.215 65.4468C109.7 66.8424 106.84 67.5105 103.959 67.3749H95.4829L100.776 51.9852H100.824L100.84 51.9376H108.795C112.439 51.9376 114.864 52.5645 116.068 53.8024C117.271 55.0402 117.384 57.0617 116.424 59.8825H116.413ZM108.692 86.2739C107.942 88.5751 106.422 90.5547 104.381 91.8857C102.155 93.269 99.5616 93.962 96.934 93.8755H86.3851L91.8206 77.8906H103.215C108.794 77.9223 110.616 80.7167 108.681 86.2739H108.692ZM131.23 44.2903C127.854 41.203 121.939 39.6306 113.483 39.573L117.065 29.1982H110.331L106.749 39.5572H102.962L106.543 29.1982H99.8093L96.2289 39.5572H79.2706L75.0564 51.9693H85.1673L71.0149 93.798H62.2375L58.0055 106.226H75.5157L72.473 115.03H79.2065L82.2492 106.226H86.0361L82.9934 115.03H89.7287L92.7714 106.226H93.0883C101.313 106.164 108.089 104.492 113.419 101.211C118.853 97.793 122.885 92.5785 124.795 86.4958C126.032 83.5872 126.159 80.3334 125.152 77.3395C124.587 76.0487 123.719 74.9101 122.619 74.0187C121.519 73.1274 120.22 72.5091 118.829 72.2154C122.205 71.1404 125.274 69.2869 127.783 66.8079C130.268 64.3163 132.138 61.2914 133.249 57.9685C135.356 51.9981 134.685 47.4375 131.237 44.2868"
        />
        <path
            className="vbl-bolt"
            d="M90.4319 23.6392L81.3644 0L61.3544 15.7243L68.2001 17.589L45.256 88.1123L33.8775 51.9447L29.9802 39.5801H0L4.24626 51.9447H18.0319L36.37 106.231H54.1491L82.4771 21.4822L90.4319 23.6392Z"
        />
    </svg>
);

export const InvestorsLoadingOverlay: React.FC<Props> = ({
    quarterLabel,
    ready,
    failed,
    stats,
    onExited,
}) => {
    const { progress, phase, statusIndex } = useLoaderProgress(
        ready,
        failed,
        STATUS_MESSAGES.length
    );

    const ringRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        ringRef.current?.style.setProperty("--p", progress.toFixed(2));
    }, [progress]);

    useEffect(() => {
        if (phase !== "hidden") return;
        const timer = window.setTimeout(onExited, EXIT_TRANSITION_MS);
        return () => window.clearTimeout(timer);
    }, [phase, onExited]);

    const celebrating = phase === "done" || phase === "hidden";
    const countingUp = celebrating && stats !== null;
    const volume = useCountUp(stats !== null ? stats.volumeEur : 0, countingUp, COUNT_UP_MS);
    const operations = useCountUp(stats !== null ? stats.operations : 0, countingUp, COUNT_UP_MS);
    const accounts = useCountUp(
        stats !== null ? stats.activeAccounts : 0,
        countingUp,
        COUNT_UP_MS
    );

    return (
        <div
            id="vbLoader"
            className={phase === "hidden" ? "vbl-hide" : undefined}
            role="status"
            aria-live="polite"
            aria-label="Loading your investor report"
        >
            <div className="vbl-bg" aria-hidden="true" />
            <div className="vbl-stage">
                <div className="vbl-ring" ref={ringRef}>
                    <div className="vbl-glow" aria-hidden="true" />
                    <div className="vbl-mark">
                        <VbMark />
                    </div>
                </div>
                <div className="vbl-pct">
                    <span>{Math.round(progress)}</span>%
                </div>
                <div className="vbl-eyebrow">VirtuaBroker · Investor intelligence</div>
                <h2 className="vbl-title">Building your {quarterLabel} report</h2>
                <div className={celebrating ? "vbl-status vbl-complete" : "vbl-status"}>
                    <span className="vbl-spinner" aria-hidden="true" />
                    <span>{celebrating ? "Report ready" : STATUS_MESSAGES[statusIndex]}</span>
                </div>
                <div className="vbl-stats">
                    <div className="vbl-stat">
                        <div className="vbl-stat-v">{fmt.moneyCompact(volume)}</div>
                        <div className="vbl-stat-l">Volume processed</div>
                    </div>
                    <div className="vbl-stat">
                        <div className="vbl-stat-v">{fmt.num(operations)}</div>
                        <div className="vbl-stat-l">Operations completed</div>
                    </div>
                    <div className="vbl-stat">
                        <div className="vbl-stat-v">{fmt.num(accounts)}</div>
                        <div className="vbl-stat-l">Active accounts</div>
                    </div>
                </div>
                <div className="vbl-bar">
                    <div className="vbl-bar-fill" style={{ width: `${progress}%` }} />
                </div>
            </div>
        </div>
    );
};
